/**
 * Date picker
 *
 * This software or document includes material copied from or derived from Date Picker Dialog
 * Example: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/examples/datepicker-dialog/
 * Copyright © 2024 World Wide Web Consortium.
 *
 * @license https://www.w3.org/copyright/software-license-2023/
 */
class DatePickerDialog {
    constructor(date_picker) {
        this.buttonLabelChoose = date_picker_localization.choose_date;
        this.buttonLabelChange = date_picker_localization.change_date;
        this.dayLabels = [
            days_long.sunday,
            days_long.monday,
            days_long.tuesday,
            days_long.wednesday,
            days_long.thursday,
            days_long.friday,
            days_long.saturday,
        ].map((value) => H_Tools.string.ucfirst(value));
        this.monthLabels = [
            months_long.january,
            months_long.february,
            months_long.march,
            months_long.april,
            months_long.may,
            months_long.june,
            months_long.july,
            months_long.august,
            months_long.september,
            months_long.october,
            months_long.november,
            months_long.december,
        ].map((value) => H_Tools.string.ucfirst(value));

        this.messageCursorKeys = date_picker_localization.cursor_keys;
        this.lastMessage = '';

        this.textboxNode = date_picker.querySelector('input[type="text"]');
        this.buttonNode = date_picker.querySelector(
            '.js-date-picker-calendar-button'
        );

        let dialog = date_picker.querySelector('.js-date-picker-dialog');
        this.dialogNode = dialog;

        this.markPassedDays =
            dialog.getAttribute('data-mark-passed-days') === 'true';

        this.messageNode = dialog.querySelector(
            '.js-date-picker-dialog-message'
        );

        this.monthYearNode = dialog.querySelector(
            '.js-date-picker-dialog-heading'
        );

        this.prevYearNode = dialog.querySelector(
            '.js-date-picker-dialog-nav-button.-prev-year'
        );
        this.prevMonthNode = dialog.querySelector(
            '.js-date-picker-dialog-nav-button.-prev-month'
        );
        this.nextMonthNode = dialog.querySelector(
            '.js-date-picker-dialog-nav-button.-next-month'
        );
        this.nextYearNode = dialog.querySelector(
            '.js-date-picker-dialog-nav-button.-next-year'
        );

        this.okButtonNode = dialog.querySelector('button[value="ok"]');
        this.cancelButtonNode = dialog.querySelector('button[value="cancel"]');

        this.tbodyNode = dialog.querySelector('table tbody');

        this.lastRowNode = null;

        this.days = [];

        this.focusDay = new Date();
        this.selectedDay = new Date(0, 0, 1);

        this.lastDate = -1;

        this.isMouseDownOnBackground = false;

        this.textboxNode.addEventListener(
            'blur',
            this.setDateForButtonLabel.bind(this)
        );
        this.textboxNode.addEventListener(
            'input',
            this.setDateForButtonLabel.bind(this)
        );

        this.buttonNode.addEventListener(
            'keydown',
            this.handleButtonKeydown.bind(this)
        );
        this.buttonNode.addEventListener(
            'click',
            this.handleButtonClick.bind(this)
        );

        this.okButtonNode.addEventListener(
            'click',
            this.handleOkButton.bind(this)
        );
        this.okButtonNode.addEventListener(
            'keydown',
            this.handleOkButton.bind(this)
        );

        this.cancelButtonNode.addEventListener(
            'click',
            this.handleCancelButton.bind(this)
        );
        this.cancelButtonNode.addEventListener(
            'keydown',
            this.handleCancelButton.bind(this)
        );

        this.prevMonthNode.addEventListener(
            'click',
            this.handlePreviousMonthButton.bind(this)
        );
        this.nextMonthNode.addEventListener(
            'click',
            this.handleNextMonthButton.bind(this)
        );
        this.prevYearNode.addEventListener(
            'click',
            this.handlePreviousYearButton.bind(this)
        );
        this.nextYearNode.addEventListener(
            'click',
            this.handleNextYearButton.bind(this)
        );

        this.prevMonthNode.addEventListener(
            'keydown',
            this.handlePreviousMonthButton.bind(this)
        );
        this.nextMonthNode.addEventListener(
            'keydown',
            this.handleNextMonthButton.bind(this)
        );
        this.prevYearNode.addEventListener(
            'keydown',
            this.handlePreviousYearButton.bind(this)
        );
        this.nextYearNode.addEventListener(
            'keydown',
            this.handleNextYearButton.bind(this)
        );

        document.body.addEventListener(
            'pointerup',
            this.handleBackgroundMouseUp.bind(this),
            true
        );

        // Create Grid of Dates

        this.tbodyNode.innerHTML = '';
        for (let i = 0; i < 6; i++) {
            const row = this.tbodyNode.insertRow(i);
            this.lastRowNode = row;
            for (let j = 0; j < 7; j++) {
                const cell = document.createElement('td');

                cell.tabIndex = -1;
                cell.addEventListener('click', this.handleDayClick.bind(this));
                cell.addEventListener(
                    'keydown',
                    this.handleDayKeyDown.bind(this)
                );
                cell.addEventListener('focus', this.handleDayFocus.bind(this));

                cell.textContent = '-1';

                row.appendChild(cell);
                this.days.push(cell);
            }
        }

        this.updateGrid();
        this.close(false);
        this.setDateForButtonLabel();
    }

    isSameDay(day1, day2) {
        return (
            day1.getFullYear() === day2.getFullYear() &&
            day1.getMonth() === day2.getMonth() &&
            day1.getDate() === day2.getDate()
        );
    }

    isNotSameMonth(day1, day2) {
        return (
            day1.getFullYear() !== day2.getFullYear() ||
            day1.getMonth() !== day2.getMonth()
        );
    }

    updateGrid() {
        // console.log('Updating grid...');

        const focusDay = this.focusDay;
        const start_of_week = parseInt(date_picker_localization.start_of_week);

        this.monthYearNode.textContent =
            this.monthLabels[focusDay.getMonth()] +
            ' ' +
            focusDay.getFullYear();

        let firstDayOfMonth = new Date(
            focusDay.getFullYear(),
            focusDay.getMonth(),
            1
        );
        let firstDayOfMonth_dayOfWeek = firstDayOfMonth.getDay();

        let firstDayOfGrid = new Date(firstDayOfMonth);
        firstDayOfGrid.setDate(
            firstDayOfMonth.getDate() -
                ((7 + firstDayOfMonth_dayOfWeek - start_of_week) % 7)
        );

        const today = new Date();
        today.setHours(0);
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);
        // console.log('today', today);

        let date = new Date(firstDayOfGrid);

        for (let i = 0; i < this.days.length; i++) {
            // console.log('Setting up grid cell for date... ', date);

            if (this.markPassedDays) {
                const hasPassed = date.getTime() < today.getTime();
                this.days[i].setAttribute(
                    'title',
                    hasPassed ? date_picker_localization.past : ''
                );
                this.days[i].classList.toggle('passed', hasPassed);
            }

            const flag = date.getMonth() !== focusDay.getMonth();
            this.updateDate(
                this.days[i],
                flag,
                date,
                this.isSameDay(date, this.selectedDay)
            );
            date.setDate(date.getDate() + 1);

            // Hide last row if all dates are disabled (e.g. in next month)
            if (i === 35) {
                if (flag) {
                    this.lastRowNode.style.visibility = 'hidden';
                } else {
                    this.lastRowNode.style.visibility = 'visible';
                }
            }
        }
    }

    updateDate(domNode, disable, day, selected) {
        let d = day.getDate().toString();
        if (day.getDate() <= 9) {
            d = '0' + d;
        }

        let m = day.getMonth() + 1;
        if (day.getMonth() < 9) {
            m = '0' + m;
        }

        domNode.tabIndex = -1;
        domNode.removeAttribute('aria-selected');
        domNode.setAttribute(
            'data-date',
            day.getFullYear() + '-' + m + '-' + d
        );

        if (disable) {
            domNode.classList.add('disabled');
            domNode.textContent = '';
        } else {
            domNode.classList.remove('disabled');
            domNode.textContent = day.getDate();
            if (selected) {
                domNode.setAttribute('aria-selected', 'true');
                domNode.tabIndex = 0;
            }
        }
    }

    moveFocusToDay(day) {
        const d = this.focusDay;

        this.focusDay = day;

        if (
            d.getMonth() !== this.focusDay.getMonth() ||
            d.getFullYear() !== this.focusDay.getFullYear()
        ) {
            this.updateGrid();
        }
        this.setFocusDay();
    }

    setFocusDay(flag) {
        if (typeof flag !== 'boolean') {
            flag = true;
        }

        for (let i = 0; i < this.days.length; i++) {
            const dayNode = this.days[i];
            const day = this.getDayFromDataDateAttribute(dayNode);

            dayNode.tabIndex = -1;
            if (this.isSameDay(day, this.focusDay)) {
                dayNode.tabIndex = 0;
                if (flag) {
                    dayNode.focus();
                }
            }
        }
    }

    open() {
        this.dialogNode.style.display = 'block';
        this.dialogNode.style.zIndex = 2;

        this.getDateFromTextbox();
        this.updateGrid();
        this.lastDate = this.focusDay.getDate();
    }

    isOpen() {
        return window.getComputedStyle(this.dialogNode).display !== 'none';
    }

    close(flag) {
        if (typeof flag !== 'boolean') {
            // Default is to move focus to combobox
            flag = true;
        }

        this.setMessage('');
        this.dialogNode.style.display = 'none';

        if (flag) {
            this.buttonNode.focus();
        }
    }

    changeMonth(currentDate, numMonths) {
        const getDays = (year, month) => new Date(year, month, 0).getDate();

        const isPrev = numMonths < 0;
        const numYears = Math.trunc(Math.abs(numMonths) / 12);
        numMonths = Math.abs(numMonths) % 12;

        const newYear = isPrev
            ? currentDate.getFullYear() - numYears
            : currentDate.getFullYear() + numYears;

        const newMonth = isPrev
            ? currentDate.getMonth() - numMonths
            : currentDate.getMonth() + numMonths;

        const newDate = new Date(newYear, newMonth, 1);

        const daysInMonth = getDays(
            newDate.getFullYear(),
            newDate.getMonth() + 1
        );

        // If lastDat is not initialized set to current date
        this.lastDate = this.lastDate ? this.lastDate : currentDate.getDate();

        if (this.lastDate > daysInMonth) {
            newDate.setDate(daysInMonth);
        } else {
            newDate.setDate(this.lastDate);
        }

        return newDate;
    }

    moveToNextYear() {
        this.focusDay = this.changeMonth(this.focusDay, 12);
        this.updateGrid();
    }

    moveToPreviousYear() {
        this.focusDay = this.changeMonth(this.focusDay, -12);
        this.updateGrid();
    }

    moveToNextMonth() {
        this.focusDay = this.changeMonth(this.focusDay, 1);
        this.updateGrid();
    }

    moveToPreviousMonth() {
        this.focusDay = this.changeMonth(this.focusDay, -1);
        this.updateGrid();
    }

    moveFocusToNextDay() {
        const d = new Date(this.focusDay);
        d.setDate(d.getDate() + 1);
        this.lastDate = d.getDate();
        this.moveFocusToDay(d);
    }

    moveFocusToNextWeek() {
        const d = new Date(this.focusDay);
        d.setDate(d.getDate() + 7);
        this.lastDate = d.getDate();
        this.moveFocusToDay(d);
    }

    moveFocusToPreviousDay() {
        const d = new Date(this.focusDay);
        d.setDate(d.getDate() - 1);
        this.lastDate = d.getDate();
        this.moveFocusToDay(d);
    }

    moveFocusToPreviousWeek() {
        const d = new Date(this.focusDay);
        d.setDate(d.getDate() - 7);
        this.lastDate = d.getDate();
        this.moveFocusToDay(d);
    }

    moveFocusToFirstDayOfWeek() {
        const d = new Date(this.focusDay);
        d.setDate(d.getDate() - d.getDay());
        this.lastDate = d.getDate();
        this.moveFocusToDay(d);
    }

    moveFocusToLastDayOfWeek() {
        const d = new Date(this.focusDay);
        d.setDate(d.getDate() + (6 - d.getDay()));
        this.lastDate = d.getDate();
        this.moveFocusToDay(d);
    }

    // Day methods

    isDayDisabled(domNode) {
        return domNode.classList.contains('disabled');
    }

    getDayFromDataDateAttribute(domNode) {
        const parts = domNode.getAttribute('data-date').split('-');
        return new Date(parts[0], parseInt(parts[1]) - 1, parts[2]);
    }

    // Textbox methods

    setTextboxDate(domNode) {
        let d = this.focusDay;

        if (domNode) {
            d = this.getDayFromDataDateAttribute(domNode);
            // updated aria-selected
            this.days.forEach((day) =>
                day === domNode
                    ? day.setAttribute('aria-selected', 'true')
                    : day.removeAttribute('aria-selected')
            );
        }

        this.textboxNode.value =
            d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear();
        this.textboxNode.dispatchEvent(new Event('change'));
        this.textboxNode.dispatchEvent(new Event('change-by-calendar'));
        this.textboxNode.dispatchEvent(new Event('input', { bubbles: true }));
    }

    getDateFromTextbox() {
        // console.log('Getting date from text box...');

        let date = H_Tools.date.toDateObject(this.textboxNode.value);

        // TODO: Move this start + end date functionality to it's own module
        if (
            this.textboxNode.name === 'end-date' &&
            !H_Tools.date.isDate(date)
        ) {
            date = H_Tools.date.toDateObject(
                this.textboxNode
                    .closest('.js-facet-accordion')
                    ?.querySelector('.js-facet-input[name=start-date]')?.value
            );
        }
        // END TODO

        if (H_Tools.date.isDate(date)) {
            if (date.year < 100) {
                date.year = 2000 + date.year;
            }
            this.focusDay = new Date(date.year, date.month - 1, date.day);
            this.selectedDay = new Date(this.focusDay);
        } else {
            // If not a valid date (MM/DD/YY) initialize with todays date
            this.focusDay = new Date();
            this.selectedDay = new Date(0, 0, 1);
        }
    }

    setDateForButtonLabel() {
        const date = H_Tools.date.toDateObject(this.textboxNode.value);

        if (H_Tools.date.isDate(date)) {
            const day = new Date(date.year, date.month - 1, date.day);

            let label = this.buttonLabelChange;
            label += ', ' + this.dayLabels[day.getDay()];
            label += ' ' + this.monthLabels[day.getMonth()];
            label += ' ' + day.getDate();
            label += ', ' + day.getFullYear();
            this.buttonNode.setAttribute('aria-label', label);
        } else {
            // If not a valid date, initialize with "Choose Date"
            this.buttonNode.setAttribute('aria-label', this.buttonLabelChoose);
        }
    }

    setMessage(str) {
        function setMessageDelayed() {
            this.messageNode.textContent = str; // jshint ignore:line
        }

        if (str !== this.lastMessage) {
            setTimeout(setMessageDelayed.bind(this), 200);
            this.lastMessage = str;
        }
    }

    // Event handlers

    handleOkButton(event) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Tab':
                        if (!event.shiftKey) {
                            this.prevYearNode.focus();
                            flag = true;
                        }
                        break;

                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    default:
                        break;
                }
                break;

            case 'click':
                this.setTextboxDate();
                this.close();
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleCancelButton(event) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    default:
                        break;
                }
                break;

            case 'click':
                this.close();
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleNextYearButton(event) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    case 'Enter':
                        this.moveToNextYear();
                        this.setFocusDay(false);
                        flag = true;
                        break;
                }

                break;

            case 'click':
                this.moveToNextYear();
                this.setFocusDay(false);
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handlePreviousYearButton(event) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Enter':
                        this.moveToPreviousYear();
                        this.setFocusDay(false);
                        flag = true;
                        break;

                    case 'Tab':
                        if (event.shiftKey) {
                            this.okButtonNode.focus();
                            flag = true;
                        }
                        break;

                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    default:
                        break;
                }

                break;

            case 'click':
                this.moveToPreviousYear();
                this.setFocusDay(false);
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleNextMonthButton(event) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    case 'Enter':
                        this.moveToNextMonth();
                        this.setFocusDay(false);
                        flag = true;
                        break;
                }

                break;

            case 'click':
                this.moveToNextMonth();
                this.setFocusDay(false);
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handlePreviousMonthButton(event) {
        let flag = false;

        switch (event.type) {
            case 'keydown':
                switch (event.key) {
                    case 'Esc':
                    case 'Escape':
                        this.close();
                        flag = true;
                        break;

                    case 'Enter':
                        this.moveToPreviousMonth();
                        this.setFocusDay(false);
                        flag = true;
                        break;
                }

                break;

            case 'click':
                this.moveToPreviousMonth();
                this.setFocusDay(false);
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleDayKeyDown(event) {
        let flag = false;

        switch (event.key) {
            case 'Esc':
            case 'Escape':
                this.close();
                break;

            case ' ':
                this.setTextboxDate(event.currentTarget);
                flag = true;
                break;

            case 'Enter':
                this.setTextboxDate(event.currentTarget);
                this.close();
                flag = true;
                break;

            case 'Tab':
                this.cancelButtonNode.focus();
                if (event.shiftKey) {
                    this.nextYearNode.focus();
                }
                this.setMessage('');
                flag = true;
                break;

            case 'Right':
            case 'ArrowRight':
                this.moveFocusToNextDay();
                flag = true;
                break;

            case 'Left':
            case 'ArrowLeft':
                this.moveFocusToPreviousDay();
                flag = true;
                break;

            case 'Down':
            case 'ArrowDown':
                this.moveFocusToNextWeek();
                flag = true;
                break;

            case 'Up':
            case 'ArrowUp':
                this.moveFocusToPreviousWeek();
                flag = true;
                break;

            case 'PageUp':
                if (event.shiftKey) {
                    this.moveToPreviousYear();
                } else {
                    this.moveToPreviousMonth();
                }
                this.setFocusDay();
                flag = true;
                break;

            case 'PageDown':
                if (event.shiftKey) {
                    this.moveToNextYear();
                } else {
                    this.moveToNextMonth();
                }
                this.setFocusDay();
                flag = true;
                break;

            case 'Home':
                this.moveFocusToFirstDayOfWeek();
                flag = true;
                break;

            case 'End':
                this.moveFocusToLastDayOfWeek();
                flag = true;
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleDayClick(event) {
        if (!this.isDayDisabled(event.currentTarget) && event.which !== 3) {
            this.setTextboxDate(event.currentTarget);
            this.close();
        }

        event.stopPropagation();
        event.preventDefault();
    }

    handleDayFocus() {
        this.setMessage(this.messageCursorKeys);
    }

    handleButtonKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            this.open();
            this.setFocusDay();

            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleButtonClick(event) {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
            this.setFocusDay();
        }

        event.stopPropagation();
        event.preventDefault();
    }

    handleBackgroundMouseUp(event) {
        if (
            !this.buttonNode.contains(event.target) &&
            !this.dialogNode.contains(event.target)
        ) {
            if (this.isOpen()) {
                this.close(false);
                event.stopPropagation();
                event.preventDefault();
            }
        }
    }
}
